import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { getTravelPlaces, TravelPlace } from 'common/api/tourApi';
import { IcCheck } from 'components/common/icons/EmojiIcons';
import {
    AppScreen, Button, ButtonFooter, PageIntro, SearchInput, Section, SectionHeader,
} from 'components/ds';
import styles from './TravelRecommendations.module.css';

interface RecommendedPlace {
    id: string;
    name: string;
    description: string;
    category: string;
    rating?: number;
    priceRange?: string;
    imageUrl?: string;
    tags: string[];
    lat?: number;
    lng?: number;
    address?: string;
}

// Sygic 카테고리 → 앱 카테고리 매핑
const CATEGORY_MAP: Record<string, string> = {
    attraction: 'sightseeing',
    restaurant: 'eating',
    cafe: 'eating',
    hotel: 'sleeping',
    shopping: 'shopping',
    sightseeing: 'sightseeing',
    eating: 'eating',
    sleeping: 'sleeping',
    discovering: 'discovering',
};

// icon 은 categoryIcon.tsx 의 카테고리 키. CategoryIcon 컴포넌트가 SVG 로 렌더.
const CATEGORY_INFO: Record<string, { icon: string; title: string }> = {
    attraction: { icon: 'attraction', title: i18n.t('createFlow.catAttraction') },
    sightseeing: { icon: 'sightseeing', title: i18n.t('createFlow.catAttraction') },
    restaurant: { icon: 'restaurant', title: i18n.t('createFlow.catRestaurant') },
    eating: { icon: 'restaurant', title: i18n.t('createFlow.catRestaurant') },
    cafe: { icon: 'cafe', title: i18n.t('createFlow.catCafe') },
    hotel: { icon: 'hotel', title: i18n.t('createFlow.catHotel') },
    sleeping: { icon: 'sleeping', title: i18n.t('createFlow.catHotel') },
    shopping: { icon: 'shopping', title: i18n.t('createFlow.catShopping') },
    discovering: { icon: 'discovering', title: i18n.t('createFlow.catMuseum') },
};

export const TravelRecommendations = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const { meetingId, category, destination } = location.state || {};
    const categoryInfo = CATEGORY_INFO[category] || { icon: 'pin', title: i18n.t('createFlow.catPlace') };

    const [selectedPlaces, setSelectedPlaces] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [recommendations, setRecommendations] = useState<RecommendedPlace[]>([]);

    // 도시 ID (sessionStorage에서 destinationInfo 참조)
    const savedData = JSON.parse(sessionStorage.getItem('meeting_create_data') || '{}');
    const cityId = savedData.destinationInfo?.regionId || savedData.selectedRegion?.id || '';

    // API에서 추천 장소 로드
    useEffect(() => {
        const loadPlaces = async () => {
            setIsLoading(true);
            const sygicCategory = CATEGORY_MAP[category] || category;
            const places = await getTravelPlaces(cityId, sygicCategory, 20);

            if (places.length > 0) {
                setRecommendations(places.map((p: TravelPlace) => ({
                    id: p.placeId || p.name,
                    name: p.name,
                    description: p.description || '',
                    category: p.category || category,
                    rating: p.rating,
                    priceRange: p.priceRange,
                    imageUrl: p.thumbnailUrl,
                    tags: p.tags ? p.tags.split(',').map((t: string) => t.trim()) : [],
                    lat: p.latitude,
                    lng: p.longitude,
                    address: p.address,
                })));
            }
            setIsLoading(false);
        };
        loadPlaces();
    }, [cityId, category]);

    const filteredRecommendations = searchQuery
        ? recommendations.filter(place =>
            place.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            place.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            place.tags.some(tag => tag.includes(searchQuery))
        )
        : recommendations;

    const handleTogglePlace = (placeId: string) => {
        setSelectedPlaces(prev =>
            prev.includes(placeId)
                ? prev.filter(id => id !== placeId)
                : [...prev, placeId]
        );
    };

    const handleSearchPlace = () => {
        // 직접 장소 검색 화면으로 이동
        navigate('/meeting/place-search', {
            state: {
                returnTo: '/meeting/travel-recommendations',
                meetingId,
                category,
                destination,
                isSubPlaceSearch: true
            }
        });
    };

    const handleAddPlaces = async () => {
        if (selectedPlaces.length === 0) {
            await showAlert({ message: t('createFlow.selectPlaceFirst') });
            return;
        }

        setIsLoading(true);

        try {
            // 선택된 장소들을 모임의 서브 장소로 등록
            const placesToAdd = recommendations.filter(p => selectedPlaces.includes(p.id));

            // TODO: API 호출로 서브 장소 등록
            // await MeetingRepository.addSubPlaces(meetingId, placesToAdd);

            console.log('Adding places to meeting:', meetingId, placesToAdd);

            await showAlert({ message: t('createFlow.placesAdded', { count: placesToAdd.length }) });

            // 완료 화면으로 돌아가기
            navigate(-1);
        } catch (error) {
            console.error('장소 추가 실패:', error);
            await showAlert({ message: t('createFlow.addPlacesFail') });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AppScreen
            title={t('createFlow.addCategory', { cat: categoryInfo.title })}
            bar="sub"
            hasFooter
        >
            <PageIntro
                title={t('createFlow.recommendTitle', { dest: destination, cat: categoryInfo.title })}
                desc={t('createFlow.selectPlaceToAdd')}
            />

            <Section spacing="tight">
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={t('createFlow.searchPlacePh')}
                    clearLabel={t('common.close')}
                />
                <div className={styles.searchAction}>
                    <Button variant="ghost" size="sm" block onClick={handleSearchPlace}>
                        {t('createFlow.searchDirect')}
                    </Button>
                </div>
            </Section>

            <Section divided last>
                <SectionHeader
                    title={t('createFlow.recommendListTitle')}
                    count={filteredRecommendations.length}
                    moreLabel={selectedPlaces.length > 0 ? t('createFlow.clearSelection') : undefined}
                    onMore={selectedPlaces.length > 0 ? () => setSelectedPlaces([]) : undefined}
                />

                {filteredRecommendations.length === 0 ? (
                    <div className={styles.empty}>
                        <span className={styles.emptyText}>{t('createFlow.noResults')}</span>
                        <div className={styles.emptyAction}>
                            <Button variant="secondary" size="lg" block onClick={handleSearchPlace}>
                                {t('createFlow.searchDirectLong')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <ul className={styles.list}>
                        {filteredRecommendations.map((place) => {
                            const on = selectedPlaces.includes(place.id);
                            return (
                                <li key={place.id}>
                                    <button
                                        type="button"
                                        className={on ? `${styles.row} ${styles.rowOn}` : styles.row}
                                        onClick={() => handleTogglePlace(place.id)}
                                        aria-pressed={on}
                                    >
                                        <span className={styles.check} aria-hidden>
                                            {on && <IcCheck size={14} />}
                                        </span>
                                        <span className={styles.rowBody}>
                                            <span className={styles.rowTop}>
                                                <span className={styles.rowTitle}>{place.name}</span>
                                                {place.rating ? (
                                                    <span className={styles.rating}>{place.rating.toFixed(1)}</span>
                                                ) : null}
                                            </span>
                                            <span className={styles.rowDesc}>{place.description}</span>
                                            <span className={styles.tags}>
                                                {place.tags.map((tag: string, idx: number) => (
                                                    <span key={idx} className={styles.tag}>#{tag}</span>
                                                ))}
                                                {place.priceRange && (
                                                    <span className={styles.tag}>{place.priceRange}</span>
                                                )}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <p className={styles.hint}>{t('createFlow.aiHint', { dest: destination })}</p>
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={selectedPlaces.length === 0 || isLoading}
                    onClick={handleAddPlaces}
                >
                    {isLoading
                        ? t('createFlow.adding')
                        : t('createFlow.addPlacesBtn', { count: selectedPlaces.length })}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
